/**
 * Conversation Memory / Custom Instructions
 * Persistent user preferences that are injected into every conversation's system prompt.
 */

import { db } from './db.js';

const MEMORY_KEY = 'memory_items';

let memoryItems = [];
let loaded = false;

/**
 * Load memory items from IndexedDB
 * @returns {Promise<Array<{id: string, content: string, createdAt: number}>>}
 */
export async function loadMemory() {
  if (loaded) return memoryItems;
  try {
    const stored = await db.loadSetting(MEMORY_KEY);
    memoryItems = stored || [];
    loaded = true;
  } catch {
    memoryItems = [];
  }
  return memoryItems;
}

/**
 * Get current memory items (sync, must call loadMemory first)
 */
export function getMemoryItems() {
  return memoryItems;
}

/**
 * Add a memory item
 * @param {string} content - The instruction/fact to remember
 */
const MAX_MEMORY_ITEMS = 30;

export async function addMemoryItem(content) {
  if (!content.trim()) return;
  if (memoryItems.length >= MAX_MEMORY_ITEMS) return null; // Cap at 30 items
  const item = {
    id: crypto.randomUUID(),
    content: content.trim(),
    createdAt: Date.now(),
  };
  memoryItems = [...memoryItems, item];
  await db.saveSetting(MEMORY_KEY, memoryItems);
  return item;
}

/**
 * Remove a memory item
 * @param {string} id - Memory item ID
 */
export async function removeMemoryItem(id) {
  memoryItems = memoryItems.filter(m => m.id !== id);
  await db.saveSetting(MEMORY_KEY, memoryItems);
}

/**
 * Update a memory item
 * @param {string} id - Memory item ID
 * @param {string} newContent - Updated content
 */
export async function updateMemoryItem(id, newContent) {
  memoryItems = memoryItems.map(m =>
    m.id === id ? { ...m, content: newContent.trim() } : m
  );
  await db.saveSetting(MEMORY_KEY, memoryItems);
}

/**
 * Build memory context string for system prompt injection
 * @returns {string} Formatted memory context or empty string
 */
export function buildMemoryContext() {
  if (memoryItems.length === 0) return '';

  const items = memoryItems.map(m => `- ${m.content}`).join('\n');
  return `[User Preferences & Memory]\nThe user has saved the following preferences and facts about themselves. Use these to personalize your responses:\n${items}\n[End User Preferences]`;
}

/**
 * Detect if the AI should suggest remembering something from a message
 * @param {string} userMessage - The user's message
 * @returns {{ shouldSuggest: boolean, suggestion: string }}
 */
export function detectMemorySuggestion(userMessage) {
  const patterns = [
    { regex: /(?:i am|i'm|i work as|my job is|my role is)\s+(.+)/i, prefix: 'User is' },
    { regex: /(?:i prefer|i like|i always|i usually|i want you to)\s+(.+)/i, prefix: 'User prefers:' },
    { regex: /(?:remember that|note that|keep in mind)\s+(.+)/i, prefix: '' },
    { regex: /(?:i use|i'm using|my stack is|i code in)\s+(.+)/i, prefix: 'User uses' },
    { regex: /(?:call me|my name is)\s+(.+)/i, prefix: 'User\'s name is' },
  ];

  for (const { regex, prefix } of patterns) {
    const match = userMessage.match(regex);
    if (match) {
      const extracted = match[1].replace(/[.!?]+$/, '').trim();
      const suggestion = prefix ? `${prefix} ${extracted}` : extracted;
      // Don't suggest if it's too long (probably not a preference)
      if (suggestion.length > 200) continue;
      // Don't suggest if already in memory
      const alreadyExists = memoryItems.some(m =>
        m.content.toLowerCase().includes(extracted.toLowerCase())
      );
      if (alreadyExists) continue;
      return { shouldSuggest: true, suggestion };
    }
  }

  return { shouldSuggest: false, suggestion: '' };
}
